import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, of, finalize } from 'rxjs';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { OrganizationType } from 'src/app/shared/enums/organization-type.enum';
import OrganizationService from 'src/app/shared/services/organization.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { whitespace } from 'src/app/shared/validators/whitespace.validator';
import { Organization } from '../../organization-models';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { KeyValuePipe } from '@angular/common';
import { TranslateOrganizationDiscountTypeEnum } from '../../../../shared/pipes/translate-organization-discount-type-enum.pipe';
import { TranslateOrganizationTypeEnum } from '../../../../shared/pipes/translate-organization-type-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AdmG4CustomerNumberComponent } from 'src/app/shared/components/adm-g4-customer-number/adm-g4-customer-number.component';

interface EditOrganizationForm {
  name: FormControl<string>;
  identification: FormControl<string | null>;
  organizationType: FormControl<OrganizationType>;
  discountType: FormControl<DiscountType>;
  logoLink: FormControl<string>;
  compactLogoLink: FormControl<string>;
  idTagInvitationActive: FormControl<boolean>;
}

@Component({
  selector: 'adm-edit-organization-dialog',
  templateUrl: './edit-organization-dialog.component.html',
  styleUrls: ['./edit-organization-dialog.component.scss'],
  imports: [
    CustomDropDownArrowDirective,
    KeyValuePipe,
    TranslateOrganizationDiscountTypeEnum,
    TranslateOrganizationTypeEnum,
    AdmG4CustomerNumberComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
})
export class EditOrganizationDialogComponent implements OnInit {
  data = inject<{
    organizationId: number;
  }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<void, number>>(MatDialogRef);
  private service = inject(OrganizationService);
  private loader = inject(TopLoaderService);
  private snackbarService = inject(SnackbarService);

  private errors: { [controlName: string]: { [error: string]: string } } = {
    name: {
      minLength: `Navn skal være på mindst 1 karakterer.`,
      maxLength: `Navn må maks være 50 karakterer.`,
      required: `Navn er påkrævet.`,
      empty: `Navn må ikke være tom.`,
    },
    identification: {
      number: `Kundenr. skal være nummerisk.`,
      minLength: `Kundenr. skal være på mindst 1 karakterer.`,
      maxLength: `Kundenr. må maks være 30 karakterer.`,
      required: `Kundenr. er påkrævet.`,
    },
    logoLink: {
      pattern: `URL'en skal pege på en .svg eller .png fil og må maks være 1mb.`,
    },
    compactLogoLink: {
      pattern: `URL'en skal pege på en .svg eller .png fil og må maks være 1mb.`,
    },
  };
  public organization: Organization | null = null;
  public dialogForm: FormGroup<EditOrganizationForm> = new FormGroup<EditOrganizationForm>({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, whitespace(), Validators.minLength(1), Validators.maxLength(50)],
    }),
    identification: new FormControl<string | null>(null, [Validators.required]),
    organizationType: new FormControl<OrganizationType>(OrganizationType.Business, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    discountType: new FormControl<DiscountType>(DiscountType.FixedAmount, { nonNullable: true, validators: [Validators.required] }),
    logoLink: new FormControl<string>('', { nonNullable: true, validators: [Validators.pattern(/^https?:\/\/.*\.(png|svg)$/i)] }),
    compactLogoLink: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(/^https?:\/\/.*\.(png|svg)$/i)],
    }),
    idTagInvitationActive: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
  });

  public organizationType: typeof OrganizationType = OrganizationType;
  public discountTypes: typeof DiscountType = DiscountType;

  ngOnInit(): void {
    this.service.getOrganization(this.data.organizationId).subscribe({
      next: resp => {
        this.organization = resp;
        this.Load();
      },
    });
  }
  private Load() {
    if (!this.organization) {
      return;
    }

    this.dialogForm.patchValue({
      name: this.organization.name,
      identification: this.organization.identification,
      organizationType: this.organization.organizationType,
      discountType: this.organization.discountType,
      logoLink: this.organization.logoLink,
      compactLogoLink: this.organization.compactLogoLink,
      idTagInvitationActive: this.organization.idTagInvitationActive,
    });
    if (this.organization.hasAttachedAccessGroups) {
      this.dialogForm.controls.discountType.disable();
    }
  }

  public save() {
    if (this.dialogForm.valid && this.organization) {
      this.loader.setLoading(true);
      this.service
        .editOrganization(this.organization.id, {
          name: this.dialogForm.controls['name'].value,
          identification: this.dialogForm.controls.identification.value,
          organizationType: this.dialogForm.controls['organizationType'].value,
          discountType: this.dialogForm.controls['discountType'].value,
          logoLink: this.dialogForm.controls['logoLink'].value,
          compactLogoLink: this.dialogForm.controls['compactLogoLink'].value,
          idTagInvitationActive: this.dialogForm.controls['idTagInvitationActive'].value,
        })
        .pipe(
          catchError((httpError: HttpErrorResponse, _) => {
            if (httpError.status === 400 && httpError.error) {
              const error = httpError.error as ErrorMessage;
              switch (error.code) {
                case 100010:
                  this.snackbarService.warning(`Organisationen kunne ikke findes`);
                  break;
                case 100002:
                case 100006:
                  this.snackbarService.warning(`Organisationsnavnet er i brug.`);
                  break;
                case 100020:
                  this.snackbarService.warning('Logoet skal være i png eller svg');
                  break;
                case 100021:
                  this.snackbarService.warning('Logoet må ikke være større end 1mb');
                  break;
                case 100022:
                  this.snackbarService.warning('Angiv venligst en gyldig url til logoet');
                  break;
              }
            } else {
              this.snackbarService.error('Der opstod en ukendt fejl ved opdatering af organisation.');
              this.dialogRef.close();
            }

            return of();
          }),
          finalize(() => this.loader.setLoading(false))
        )
        .subscribe(() => {
          this.snackbarService.success(`Organisationen blev opdateret.`);
          const organizationId = this.organization?.id;
          if (organizationId != null) {
            this.dialogRef.close(organizationId);
          }
        });
    }
  }

  public getControlErrorMessage(controlName: string, error: string) {
    return this.errors[controlName][error];
  }
}
